/** Toast notifications (Radix Toast: announced to screen readers, swipe to dismiss). */
import { Toast as ToastPrimitive } from 'radix-ui';
import { XIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useUiTranslation } from '../i18n/ui_i18n';
import { classNames } from '../styling/class_names';

export type ToastTone = 'info' | 'good' | 'warn' | 'bad';

export interface ToastRequest {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds; default 5000. */
  durationMs?: number;
  action?: { label: string; onSelect: () => void };
}

interface VisibleToast extends ToastRequest {
  toastId: number;
}

interface ToastContextValue {
  showToast: (toastRequest: ToastRequest) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneBorderClasses: Record<ToastTone, string> = {
  info: 'border-l-primary',
  good: 'border-l-good',
  warn: 'border-l-warn',
  bad: 'border-l-bad',
};

const DEFAULT_TOAST_DURATION_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t: translate } = useUiTranslation();
  const [visibleToasts, setVisibleToasts] = useState<VisibleToast[]>([]);
  const nextToastIdRef = useRef(1);

  const showToast = useCallback((toastRequest: ToastRequest) => {
    const toastId = nextToastIdRef.current;
    nextToastIdRef.current += 1;
    setVisibleToasts((currentToasts) => [...currentToasts, { ...toastRequest, toastId }]);
  }, []);

  const removeToast = useCallback((toastId: number) => {
    setVisibleToasts((currentToasts) => currentToasts.filter((toast) => toast.toastId !== toastId));
  }, []);

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {visibleToasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.toastId}
            duration={toast.durationMs ?? DEFAULT_TOAST_DURATION_MS}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                removeToast(toast.toastId);
              }
            }}
            className={classNames(
              'flex items-start gap-3 rounded-control border border-l-4 border-hair bg-card p-3 text-ink shadow-overlay',
              toneBorderClasses[toast.tone ?? 'info'],
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <ToastPrimitive.Title className="font-medium">{toast.title}</ToastPrimitive.Title>
              {toast.description === undefined ? null : (
                <ToastPrimitive.Description className="text-sm text-muted">
                  {toast.description}
                </ToastPrimitive.Description>
              )}
            </div>
            {toast.action === undefined ? null : (
              <ToastPrimitive.Action
                altText={toast.action.label}
                onClick={toast.action.onSelect}
                className="cursor-pointer text-sm font-medium text-primary"
              >
                {toast.action.label}
              </ToastPrimitive.Action>
            )}
            <ToastPrimitive.Close aria-label={translate('close')} className="cursor-pointer text-muted">
              <XIcon className="size-4" aria-hidden />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-20 right-4 z-[60] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2 outline-none md:bottom-4" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export class MissingToastProviderError extends Error {
  constructor() {
    super('useToast must be used inside <ToastProvider>');
    this.name = 'MissingToastProviderError';
  }
}

export function useToast(): ToastContextValue {
  const toastContextValue = useContext(ToastContext);
  if (toastContextValue === null) {
    throw new MissingToastProviderError();
  }
  return toastContextValue;
}
